import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import YouTube from 'react-youtube';
import { ValuesType } from 'utility-types';
import { seconds } from '~/interfaces';
import { VideoState } from '~/modules/Elements/VideoPlayer/VideoState';
import useUnstuckYouTubePlayer from '~/modules/hooks/useUnstuckYouTubePlayer';


const stateMap = {
  [YouTube.PlayerState.UNSTARTED]: VideoState.UNSTARTED,
  [YouTube.PlayerState.ENDED]: VideoState.ENDED,
  [YouTube.PlayerState.PLAYING]: VideoState.PLAYING,
  [YouTube.PlayerState.PAUSED]: VideoState.PAUSED,
  [YouTube.PlayerState.BUFFERING]: VideoState.BUFFERING,
  [YouTube.PlayerState.CUED]: VideoState.CUED,
} as const;

interface Props {
  video: string;
  autoplay?: boolean;
  controls?: boolean;
  disablekb?: boolean;
  volume?: number;
  startAt?: number;
  onStateChange?: (state: VideoState) => void;
  width: number;
  height: number;
}

export interface LoadVideByIdOpts {
  videoId: string;
  startSeconds: number;
  endSeconds: number;
}

export interface VideoPlayerRef {
  getStatus: () => ValuesType<typeof stateMap>;
  seekTo: (time: seconds) => void;
  setPlaybackSpeed: (speed: number) => void;
  setVolume: (newVolume: number) => void;
  getCurrentTime: () => Promise<seconds>;
  loadVideoById: (opts: LoadVideByIdOpts) => void;
  setSize: (w: number, h: number) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  getDuration: () => Promise<number>;
}

function clamp01(n: number) {
  return Math.min(1, Math.max(0, n));
}

export default forwardRef(function YoutubeVideoPlayer(
  props: Props,
  ref: ForwardedRef<VideoPlayerRef>,
) {
  const { video, autoplay, startAt, volume, width, height, onStateChange } = props;
  const player = useRef<YouTube | null>(null);
  const [currentStatus, setCurrentStatus] = useState(YouTube.PlayerState.UNSTARTED);
  const [isReady, setIsReady] = useState(false);

  // Si este hook fuerza remount cuando está "stuck", con startAt NaN lo mataba.
  const playerKey = useUnstuckYouTubePlayer(player, currentStatus);

  const safeStartAt = useMemo(() => {
    const n = Number(startAt);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
  }, [startAt]);

  const safeVolume = useMemo(() => {
    const v = volume ?? 0.5;
    return Math.round(clamp01(v) * 100);
  }, [volume]);

  // ✅ Set volume SOLO cuando el iframe esté ready
  useEffect(() => {
    if (!isReady) return;
    try {
      const internal = player.current?.getInternalPlayer?.();
      internal?.setVolume?.(safeVolume);
    } catch (_e) {
      // no rompemos el render si el iframe se recrea
      // console.debug('[YT] setVolume failed', e);
    }
  }, [isReady, safeVolume]);

  useImperativeHandle(ref, () => ({
    getStatus: () => stateMap[currentStatus],
    setSize: (w, h) => player.current?.getInternalPlayer?.()?.setSize?.(w, h),
    seekTo: (timeSec: seconds) => player.current?.getInternalPlayer?.()?.seekTo?.(timeSec, true),
    setPlaybackSpeed: (speed: number) => player.current?.getInternalPlayer?.()?.setPlaybackRate?.(Number(speed)),
    setVolume: (newVolume: number) => player.current?.getInternalPlayer?.()?.setVolume?.(newVolume),
    getCurrentTime: () => player.current?.getInternalPlayer?.()?.getCurrentTime?.() ?? Promise.resolve(0),
    loadVideoById: (opts) => player.current?.getInternalPlayer?.()?.loadVideoById?.(opts),
    playVideo: () => player.current?.getInternalPlayer?.()?.playVideo?.(),
    pauseVideo: () => player.current?.getInternalPlayer?.()?.pauseVideo?.(),
    getDuration: () => player.current?.getInternalPlayer?.()?.getDuration?.() ?? Promise.resolve(0),
  }));

  return (
    <YouTube
      title="YouTube video player"
      key={`${video}-${playerKey}`}
      ref={player}
      videoId={video}
      opts={{
        width,
        height,
        playerVars: {
          autoplay: autoplay ? 1 : 0,
          rel: 0,
          fs: 1,
          controls: 1,
          start: safeStartAt, // ✅ nunca NaN
          disablekb: 0,
          playsinline: 1,
        },
      }}
      onReady={() => {
        setIsReady(true);
        // set volume justo al ready (extra seguro)
        try {
          const internal = player.current?.getInternalPlayer?.();
          internal?.setVolume?.(safeVolume);
        } catch { }
      }}
      onStateChange={(e) => {
        setCurrentStatus(e.data);
        onStateChange?.(stateMap[e.data]);

        // opcional: si entra a PLAYING y por lo que sea el volumen no se aplicó, lo reintentas
        if (isReady && e.data === YouTube.PlayerState.PLAYING) {
          try {
            const internal = player.current?.getInternalPlayer?.();
            internal?.setVolume?.(safeVolume);
          } catch { }
        }
      }}
      onError={(e) => {
        // esto es MUY útil para descartar restricciones reales
        console.error('[YT] onError', e.data, 'video=', video, 'start=', safeStartAt);
      }}
    />
  );
});
